import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Tabs,
  Tab,
  TextField,
  Alert,
  CircularProgress,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Checkbox,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth, useClubs } from '../context/AppContext';
import { apiClient } from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`manage-tabpanel-${index}`}
      aria-labelledby={`manage-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface Application {
  id: string;
  applicant: {
    id: string;
    name: string;
    email: string;
    branch: string;
    profilePicture?: string;
  };
  score: number;
  percentage: number;
  totalQuestions: number;
  status: 'pending' | 'approved' | 'rejected';
  appliedAt: string;
  quizAnswers?: Array<{
    questionId: string;
    selectedAnswer?: number;
    textAnswer?: string;
  }>;
}

interface QuizQuestion {
  id?: string;
  question: string;
  type?: 'mcq' | 'paragraph';
  options: string[];
  correctAnswer: number;
}

const ManageClubPage: React.FC = () => {
  const { clubId } = useParams<{ clubId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { clubs, refreshClubs } = useClubs();

  // Parse tab query parameter from URL
  const searchParams = new URLSearchParams(location.search);
  const initialTab = parseInt(searchParams.get('tab') || '0');
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Club details state
  const [clubName, setClubName] = useState('');
  const [clubDescription, setClubDescription] = useState('');
  const [clubLogo, setClubLogo] = useState('');

  // Quiz state
  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([]);
  const [editingQuiz, setEditingQuiz] = useState(false);

  // Applications state
  const [applications, setApplications] = useState<Application[]>([]);

  // Debug logging
  console.log('ManageClubPage - Current state:', {
    clubId,
    activeTab,
    loading,
    quizQuestionsCount: quizQuestions.length,
    applicationsCount: applications.length
  });
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [viewAnswersDialogOpen, setViewAnswersDialogOpen] = useState(false);
  const [viewingApplication, setViewingApplication] = useState<Application | null>(null);
  const [processingApplicationId, setProcessingApplicationId] = useState<string | null>(null);
  const [selectedApplicationIds, setSelectedApplicationIds] = useState<Set<string>>(new Set());
  const [processingBulk, setProcessingBulk] = useState(false);

  const club = clubs.find(c => c.id === clubId);

  // Check permissions based on role and permissions object
  // Faculty always has full permissions
  // Ambassadors need canManageClubs permission for full access
  const isFaculty = user?.role === 'Faculty';
  const isAmbassador = user?.role === 'Ambassador';
  const hasManageClubsPermission = user?.permissions?.canManageClubs === true;

  // Full management permissions: Faculty OR Ambassador with canManageClubs permission
  const canManageApplications = isFaculty || (isAmbassador && hasManageClubsPermission);
  const canEditQuiz = isFaculty || (isAmbassador && hasManageClubsPermission);

  useEffect(() => {
    // Always load club data from API
    loadClubData();
  }, [clubId]);

  const loadClubData = async () => {
    if (!clubId) return;

    try {
      setLoading(true);
      setError(null);

      // Load club details (includes quiz)
      const clubResponse = await apiClient.get(`/clubs/${clubId}`);
      console.log('Club response:', clubResponse.data);

      // Check if response has success wrapper or is direct data
      const clubData = clubResponse.data.success ? clubResponse.data.data : clubResponse.data;

      if (clubData) {
        // Set club basic details
        setClubName(clubData.name || '');
        setClubDescription(clubData.description || '');
        setClubLogo(clubData.logo || '');

        // Set quiz questions
        if (clubData.quiz && Array.isArray(clubData.quiz)) {
          console.log('Setting quiz questions:', clubData.quiz);
          setQuizQuestions(clubData.quiz);
        } else {
          console.log('No quiz questions found, setting empty array');
          setQuizQuestions([]);
        }
      }

      // Load applications
      const appsResponse = await apiClient.get(`/clubs/${clubId}/applications`);
      console.log('Applications response:', appsResponse.data);

      // Check if response has success wrapper or is direct data array
      const appsData = appsResponse.data.success ? appsResponse.data.data : appsResponse.data;

      if (appsData && Array.isArray(appsData)) {
        console.log('Setting applications:', appsData);
        setApplications(appsData);
      } else {
        console.log('No applications found, setting empty array');
        setApplications([]);
      }

      setLoading(false);
    } catch (error: any) {
      console.error('Error loading club data:', error);
      setError('Failed to load club data');
      setLoading(false);
    }
  };

  const handleSaveDetails = async () => {
    if (!clubId) return;

    try {
      setSaving(true);
      setError(null);

      const response = await apiClient.put(`/clubs/${clubId}`, {
        name: clubName,
        description: clubDescription,
        logo: clubLogo,
      });

      if (response.data.success) {
        setSuccess('Club details updated successfully');
        await refreshClubs();
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (error: any) {
      console.error('Error updating club:', error);
      setError(error.response?.data?.message || 'Failed to update club details');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveQuiz = async () => {
    if (!clubId) return;

    try {
      setSaving(true);
      setError(null);

      // Validate quiz questions before saving
      for (let i = 0; i < quizQuestions.length; i++) {
        const question = quizQuestions[i];

        // Check if question text is empty
        if (!question.question || !question.question.trim()) {
          setError(`Question ${i + 1}: Question text is required`);
          setSaving(false);
          return;
        }

        // For MCQ questions, validate options
        if (question.type === 'mcq' || !question.type) {
          const validOptions = question.options.filter(opt => opt && opt.trim());
          if (validOptions.length < 2) {
            setError(`Question ${i + 1}: MCQ questions must have at least 2 options filled in`);
            setSaving(false);
            return;
          }
          if (validOptions.length > 4) {
            setError(`Question ${i + 1}: MCQ questions can have maximum 4 options`);
            setSaving(false);
            return;
          }
        }
      }

      console.log('=== Saving Quiz ===');
      console.log('Quiz questions being sent:', JSON.stringify(quizQuestions, null, 2));

      const response = await apiClient.put(`/clubs/${clubId}/quiz`, {
        quiz: quizQuestions,
      });

      if (response.data.success) {
        setSuccess('Quiz updated successfully');
        setEditingQuiz(false);
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (error: any) {
      console.error('Error updating quiz:', error);
      setError(error.response?.data?.message || 'Failed to update quiz');
    } finally {
      setSaving(false);
    }
  };

  const handleApproveApplication = async (applicationId: string) => {
    if (!clubId) return;

    try {
      setProcessingApplicationId(applicationId);
      const response = await apiClient.post(
        `/clubs/${clubId}/applications/${applicationId}/approve`
      );

      if (response.data.success) {
        setSuccess('Application approved successfully');
        // Reload applications data to get updated status from backend
        const appsResponse = await apiClient.get(`/clubs/${clubId}/applications`);
        const appsData = appsResponse.data.success ? appsResponse.data.data : appsResponse.data;
        if (appsData && Array.isArray(appsData)) {
          setApplications(appsData);
        }
        await refreshClubs();
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (error: any) {
      console.error('Error approving application:', error);
      setError(error.response?.data?.message || 'Failed to approve application');
    } finally {
      setProcessingApplicationId(null);
    }
  };

  const handleRejectApplication = async () => {
    if (!clubId || !selectedApplication) return;

    try {
      setProcessingApplicationId(selectedApplication.id);
      const response = await apiClient.post(
        `/clubs/${clubId}/applications/${selectedApplication.id}/reject`,
        { reason: rejectReason }
      );

      if (response.data.success) {
        setSuccess('Application rejected');
        setRejectDialogOpen(false);
        setSelectedApplication(null);
        setRejectReason('');
        // Reload applications data to get updated status from backend
        const appsResponse = await apiClient.get(`/clubs/${clubId}/applications`);
        const appsData = appsResponse.data.success ? appsResponse.data.data : appsResponse.data;
        if (appsData && Array.isArray(appsData)) {
          setApplications(appsData);
        }
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (error: any) {
      console.error('Error rejecting application:', error);
      setError(error.response?.data?.message || 'Failed to reject application');
    } finally {
      setProcessingApplicationId(null);
    }
  };

  const handleBulkApprove = async () => {
    if (!clubId || selectedApplicationIds.size === 0) return;

    try {
      setProcessingBulk(true);
      const selectedIds = Array.from(selectedApplicationIds);
      let successCount = 0;
      let failCount = 0;

      for (const appId of selectedIds) {
        try {
          await apiClient.post(`/clubs/${clubId}/applications/${appId}/approve`);
          successCount++;
        } catch (error) {
          failCount++;
        }
      }

      if (successCount > 0) {
        setSuccess(`${successCount} application(s) approved successfully`);
      }
      if (failCount > 0) {
        setError(`${failCount} application(s) failed to approve`);
      }

      // Reload applications data
      const appsResponse = await apiClient.get(`/clubs/${clubId}/applications`);
      const appsData = appsResponse.data.success ? appsResponse.data.data : appsResponse.data;
      if (appsData && Array.isArray(appsData)) {
        setApplications(appsData);
      }
      await refreshClubs();
      setSelectedApplicationIds(new Set());
      setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 3000);
    } catch (error: any) {
      console.error('Error bulk approving:', error);
      setError('Failed to approve applications');
    } finally {
      setProcessingBulk(false);
    }
  };

  const handleRefreshApplications = async () => {
    await loadClubData();
  };

  const handleToggleSelectAll = () => {
    const pendingApps = applications.filter(app => app.status === 'pending');
    if (selectedApplicationIds.size === pendingApps.length) {
      setSelectedApplicationIds(new Set());
    } else {
      setSelectedApplicationIds(new Set(pendingApps.map(app => app.id)));
    }
  };

  const handleToggleApplication = (appId: string) => {
    const newSelected = new Set(selectedApplicationIds);
    if (newSelected.has(appId)) {
      newSelected.delete(appId);
    } else {
      newSelected.add(appId);
    }
    setSelectedApplicationIds(newSelected);
  };

  const addQuizQuestion = (type: 'mcq' | 'paragraph' = 'mcq') => {
    setQuizQuestions([
      ...quizQuestions,
      {
        question: '',
        type: type,
        options: type === 'mcq' ? ['', '', '', ''] : [],
        correctAnswer: 0,
      },
    ]);
  };

  const updateQuizQuestion = (index: number, field: string, value: any) => {
    const updated = [...quizQuestions];
    if (field === 'type') {
      // When changing type, reset options appropriately
      updated[index] = {
        ...updated[index],
        type: value,
        options: value === 'mcq' ? ['', '', '', ''] : [],
        correctAnswer: 0,
      };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setQuizQuestions(updated);
  };

  const updateQuizOption = (qIndex: number, optIndex: number, value: string) => {
    const updated = [...quizQuestions];
    updated[qIndex].options[optIndex] = value;
    setQuizQuestions(updated);
  };

  const deleteQuizQuestion = (index: number) => {
    setQuizQuestions(quizQuestions.filter((_, i) => i !== index));
  };

  // Only show "not found" if loading is complete and we still have no club name
  if (!loading && !clubName && error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Club not found
        </Typography>
      </Box>
    );
  }

  // Check if user is faculty mentor
  if (user?.role !== 'Faculty' && user?.role !== 'Ambassador') {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Only faculty mentors and ambassadors can manage clubs
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f0f2f5', pb: 4 }}>
      {/* Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backgroundColor: 'white',
          borderBottom: '1px solid #e4e6ea',
          px: { xs: 2, sm: 3 },
          py: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
          <IconButton onClick={() => navigate(`/clubs/${clubId}`)} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Manage Club
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {clubName || 'Loading...'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3 } }}>
        {error && (
          <Alert severity="error" sx={{ mt: 3, mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 3, mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Tabs */}
        <Card sx={{ mt: 3, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              px: { xs: 1, sm: 2 },
              '& .MuiTab-root': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minWidth: { xs: 'auto', sm: 160 },
                px: { xs: 1, sm: 2 }
              }
            }}
          >
            <Tab label="Club Details" />
            <Tab label="Quiz" />
            <Tab label={`Applications (${applications.filter(a => a.status === 'pending').length})`} />
          </Tabs>

          {/* Club Details Tab */}
          <TabPanel value={activeTab} index={0}>
            <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
              <TextField
                fullWidth
                label="Club Name"
                value={clubName}
                onChange={(e) => setClubName(e.target.value)}
                sx={{ mb: 3 }}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                value={clubDescription}
                onChange={(e) => setClubDescription(e.target.value)}
                sx={{ mb: 3 }}
              />
              <TextField
                fullWidth
                label="Logo (Emoji or URL)"
                value={clubLogo}
                onChange={(e) => setClubLogo(e.target.value)}
                sx={{ mb: 3 }}
              />
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveDetails}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </CardContent>
          </TabPanel>

          {/* Quiz Tab */}
          <TabPanel value={activeTab} index={1}>
            <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: { xs: 2, sm: 0 }, mb: 3 }}>
                <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                  Entry Quiz
                  {isAmbassador && !hasManageClubsPermission && (
                    <Chip
                      label="Read Only"
                      size="small"
                      color="default"
                      sx={{ ml: 2, fontSize: '0.7rem' }}
                    />
                  )}
                </Typography>
                {canEditQuiz && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 2 } }}>
                    {editingQuiz && (
                      <>
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon sx={{ display: { xs: 'none', sm: 'block' } }} />}
                          onClick={() => addQuizQuestion('mcq')}
                          size="small"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          Add MCQ
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon sx={{ display: { xs: 'none', sm: 'block' } }} />}
                          onClick={() => addQuizQuestion('paragraph')}
                          size="small"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          Add Paragraph
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={<SaveIcon sx={{ display: { xs: 'none', sm: 'block' } }} />}
                          onClick={handleSaveQuiz}
                          disabled={saving}
                          size="small"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          Save Quiz
                        </Button>
                      </>
                    )}
                    {!editingQuiz && (
                      <Button
                        variant="contained"
                        startIcon={<EditIcon sx={{ display: { xs: 'none', sm: 'block' } }} />}
                        onClick={() => setEditingQuiz(true)}
                        size="small"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        Edit Quiz
                      </Button>
                    )}
                  </Box>
                )}
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : quizQuestions.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                    No quiz questions yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Add questions to create an entry quiz for new members
                  </Typography>
                  {editingQuiz && (
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'center' }}>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => addQuizQuestion('mcq')}
                        size="small"
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                      >
                        Add MCQ Question
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => addQuizQuestion('paragraph')}
                        size="small"
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                      >
                        Add Paragraph Question
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box>
                  {quizQuestions.map((question, qIndex) => (
                    <Card key={qIndex} sx={{ mb: 3, p: { xs: 1.5, sm: 2 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Question {qIndex + 1}
                          </Typography>
                          <Chip
                            label={question.type === 'paragraph' ? 'Paragraph' : 'MCQ'}
                            size="small"
                            color={question.type === 'paragraph' ? 'secondary' : 'primary'}
                            onClick={editingQuiz ? () => updateQuizQuestion(qIndex, 'type', question.type === 'paragraph' ? 'mcq' : 'paragraph') : undefined}
                            sx={editingQuiz ? { cursor: 'pointer' } : undefined}
                          />
                        </Box>
                        {editingQuiz && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => deleteQuizQuestion(qIndex)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                      <TextField
                        fullWidth
                        label="Question"
                        value={question.question}
                        onChange={(e) =>
                          updateQuizQuestion(qIndex, 'question', e.target.value)
                        }
                        disabled={!editingQuiz}
                        sx={{ mb: 2 }}
                      />
                      {question.type === 'paragraph' ? (
                        <Alert severity="info" sx={{ mt: 1 }}>
                          This is a paragraph answer question. Students will provide written responses that will need manual review.
                        </Alert>
                      ) : (
                        <>
                          {question.options.map((option, optIndex) => (
                            <TextField
                              key={optIndex}
                              fullWidth
                              label={`Option ${optIndex + 1}`}
                              value={option}
                              onChange={(e) =>
                                updateQuizOption(qIndex, optIndex, e.target.value)
                              }
                              disabled={!editingQuiz}
                              sx={{ mb: 1 }}
                              InputProps={{
                                endAdornment:
                                  question.correctAnswer === optIndex ? (
                                    <Chip label="Correct" color="success" size="small" />
                                  ) : editingQuiz ? (
                                    <Button
                                      size="small"
                                      onClick={() =>
                                        updateQuizQuestion(qIndex, 'correctAnswer', optIndex)
                                      }
                                    >
                                      Mark Correct
                                    </Button>
                                  ) : null,
                              }}
                            />
                          ))}
                        </>
                      )}
                    </Card>
                  ))}
                </Box>
              )}
            </CardContent>
          </TabPanel>

          {/* Applications Tab */}
          <TabPanel value={activeTab} index={2}>
            <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
              {/* Ambassador Info Banner - Only show if they don't have manage permission */}
              {isAmbassador && !hasManageClubsPermission && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <strong>Ambassador View:</strong> You can view applications but only users with club management permission can approve or reject them.
                </Alert>
              )}

              {/* Bulk Actions Toolbar - For users with management permission */}
              {canManageApplications && applications.filter(a => a.status === 'pending').length > 0 && (
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Checkbox checked={selectedApplicationIds.size === applications.filter(a => a.status === 'pending').length && applications.filter(a => a.status === 'pending').length > 0} />}
                    onClick={handleToggleSelectAll}
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    {selectedApplicationIds.size === applications.filter(a => a.status === 'pending').length && applications.filter(a => a.status === 'pending').length > 0
                      ? 'Deselect All'
                      : 'Select All Pending'}
                  </Button>
                  {selectedApplicationIds.size > 0 && (
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={processingBulk ? <CircularProgress size={16} color="inherit" /> : <ApproveIcon />}
                      onClick={handleBulkApprove}
                      disabled={processingBulk}
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      {processingBulk ? 'Approving...' : `Approve Selected (${selectedApplicationIds.size})`}
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefreshApplications}
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, ml: 'auto' }}
                  >
                    Refresh
                  </Button>
                </Box>
              )}

              {/* Refresh Button for Ambassadors without management permission */}
              {isAmbassador && !hasManageClubsPermission && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefreshApplications}
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    Refresh
                  </Button>
                </Box>
              )}
              {/* Application Stats */}
              <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, mb: 3, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                <Card sx={{ flex: 1, minWidth: { xs: 'calc(50% - 4px)', sm: 'auto' }, bgcolor: 'warning.50' }}>
                  <CardContent sx={{ textAlign: 'center', py: { xs: 1.5, sm: 2 }, px: { xs: 1, sm: 2 } }}>
                    <Typography variant="h4" color="warning.main" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                      {applications.filter(a => a.status === 'pending').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Pending
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1, minWidth: { xs: 'calc(50% - 4px)', sm: 'auto' }, bgcolor: 'success.50' }}>
                  <CardContent sx={{ textAlign: 'center', py: { xs: 1.5, sm: 2 }, px: { xs: 1, sm: 2 } }}>
                    <Typography variant="h4" color="success.main" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                      {applications.filter(a => a.status === 'approved').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Approved
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1, minWidth: { xs: '100%', sm: 'auto' }, bgcolor: 'error.50' }}>
                  <CardContent sx={{ textAlign: 'center', py: { xs: 1.5, sm: 2 }, px: { xs: 1, sm: 2 } }}>
                    <Typography variant="h4" color="error.main" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                      {applications.filter(a => a.status === 'rejected').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Rejected
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : applications.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="h6" color="text.secondary">
                    No applications yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Applications will appear here once students complete the quiz
                  </Typography>
                </Box>
              ) : (
                <List>
                  {applications.map((app, index) => (
                    <React.Fragment key={app.id}>
                      <ListItem
                        sx={{
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          py: { xs: 1.5, sm: 2 },
                          px: { xs: 1, sm: 2 },
                          bgcolor: app.status === 'pending' ? 'action.hover' : 'transparent',
                          borderRadius: 1,
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            width: '100%',
                            alignItems: 'center',
                            mb: 1,
                            gap: { xs: 1, sm: 2 },
                          }}
                        >
                          {canManageApplications && app.status === 'pending' && (
                            <Checkbox
                              checked={selectedApplicationIds.has(app.id)}
                              onChange={() => handleToggleApplication(app.id)}
                              disabled={processingBulk || processingApplicationId !== null}
                              sx={{ p: 0.5 }}
                            />
                          )}
                          <ListItemAvatar sx={{ minWidth: { xs: 40, sm: 56 } }}>
                            <Avatar sx={{ bgcolor: '#1877f2', width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 }, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                              {app.applicant.name.charAt(0)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                {app.applicant.name}
                              </Typography>
                            }
                            secondary={
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {app.applicant.email} • {app.applicant.branch}
                              </Typography>
                            }
                            sx={{ flex: 1, minWidth: 0 }}
                          />
                          <Chip
                            label={app.status.toUpperCase()}
                            color={
                              app.status === 'approved'
                                ? 'success'
                                : app.status === 'rejected'
                                ? 'error'
                                : 'warning'
                            }
                            size="small"
                            sx={{ fontWeight: 600, fontSize: { xs: '0.65rem', sm: '0.75rem' }, flexShrink: 0 }}
                          />
                        </Box>
                        <Box sx={{ width: '100%', pl: { xs: 0, sm: 7 } }}>
                          <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3 }, mb: 1, flexWrap: 'wrap' }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                Quiz Score
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
                                {app.score}/{app.totalQuestions} ({app.percentage}%)
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                Applied Date
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
                                {new Date(app.appliedAt).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ mt: { xs: 1.5, sm: 2 }, display: 'flex', gap: 1, flexWrap: 'wrap', width: '100%' }}>
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              startIcon={<ViewIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, display: { xs: 'none', sm: 'block' } }} />}
                              onClick={() => {
                                setViewingApplication(app);
                                setViewAnswersDialogOpen(true);
                              }}
                              sx={{
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                flex: { xs: '1 1 100%', sm: '0 1 auto' },
                                minWidth: { xs: 'auto', sm: 'fit-content' }
                              }}
                            >
                              View Answers
                            </Button>
                            {canManageApplications && app.status === 'pending' && (
                              <>
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  startIcon={processingApplicationId === app.id ? <CircularProgress size={16} color="inherit" /> : <ApproveIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, display: { xs: 'none', sm: 'block' } }} />}
                                  onClick={() => handleApproveApplication(app.id)}
                                  disabled={processingApplicationId === app.id}
                                  sx={{
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    flex: { xs: '1 1 calc(50% - 4px)', sm: '0 1 auto' },
                                    minWidth: { xs: 'auto', sm: 'fit-content' }
                                  }}
                                >
                                  {processingApplicationId === app.id ? 'Approving...' : 'Approve'}
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  startIcon={<RejectIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, display: { xs: 'none', sm: 'block' } }} />}
                                  onClick={() => {
                                    setSelectedApplication(app);
                                    setRejectDialogOpen(true);
                                  }}
                                  disabled={processingApplicationId !== null}
                                  sx={{
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    flex: { xs: '1 1 calc(50% - 4px)', sm: '0 1 auto' },
                                    minWidth: { xs: 'auto', sm: 'fit-content' }
                                  }}
                                >
                                  Reject
                                </Button>
                              </>
                            )}
                            {canManageApplications && app.status === 'rejected' && (
                              <Button
                                variant="contained"
                                color="warning"
                                size="small"
                                startIcon={processingApplicationId === app.id ? <CircularProgress size={16} color="inherit" /> : <ApproveIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, display: { xs: 'none', sm: 'block' } }} />}
                                onClick={() => handleApproveApplication(app.id)}
                                disabled={processingApplicationId === app.id}
                                sx={{
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                  flex: { xs: '1 1 100%', sm: '0 1 auto' },
                                  minWidth: { xs: 'auto', sm: 'fit-content' }
                                }}
                              >
                                {processingApplicationId === app.id ? 'Approving...' : 'Reconsider & Approve'}
                              </Button>
                            )}
                            {app.status === 'approved' && (
                              <Chip
                                label="Approved ✓"
                                color="success"
                                size="small"
                                sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                              />
                            )}
                          </Box>
                        </Box>
                      </ListItem>
                      {index < applications.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </TabPanel>
        </Card>
      </Box>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => processingApplicationId ? undefined : setRejectDialogOpen(false)}>
        <DialogTitle>Reject Application</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to reject {selectedApplication?.applicant.name}'s application?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason (Optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Provide a reason for rejection..."
            disabled={processingApplicationId !== null}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={processingApplicationId !== null}>Cancel</Button>
          <Button
            onClick={handleRejectApplication}
            color="error"
            variant="contained"
            disabled={processingApplicationId !== null}
            startIcon={processingApplicationId !== null ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {processingApplicationId !== null ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Answers Dialog */}
      <Dialog
        open={viewAnswersDialogOpen}
        onClose={() => setViewAnswersDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Quiz Answers - {viewingApplication?.applicant.name}</Typography>
            <Chip
              label={`Score: ${viewingApplication?.score}/${viewingApplication?.totalQuestions} (${viewingApplication?.percentage}%)`}
              color={viewingApplication && viewingApplication.percentage >= 70 ? 'success' : 'warning'}
            />
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '60vh' }}>
          {viewingApplication && quizQuestions.length > 0 && viewingApplication.quizAnswers ? (
            <Box>
              {quizQuestions.map((question, index) => {
                const answer = viewingApplication.quizAnswers?.[index];
                const isCorrect = question.type === 'mcq' && answer?.selectedAnswer === question.correctAnswer;

                return (
                  <Card key={index} sx={{ mb: 2, bgcolor: isCorrect ? 'success.50' : 'background.paper' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
                          Q{index + 1}. {question.question}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                          <Chip
                            label={question.type === 'paragraph' ? 'Paragraph' : 'MCQ'}
                            size="small"
                            color={question.type === 'paragraph' ? 'secondary' : 'primary'}
                          />
                          {question.type === 'mcq' && (
                            <Chip
                              label={isCorrect ? 'Correct' : 'Wrong'}
                              size="small"
                              color={isCorrect ? 'success' : 'error'}
                            />
                          )}
                        </Box>
                      </Box>

                      {question.type === 'paragraph' ? (
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                            Student's Answer:
                          </Typography>
                          <Box sx={{
                            p: 2,
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}>
                            <Typography variant="body2">
                              {answer?.textAnswer || 'No answer provided'}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Box>
                          {question.options.map((option, optIndex) => {
                            const isSelected = answer?.selectedAnswer === optIndex;
                            const isCorrectOption = question.correctAnswer === optIndex;

                            return (
                              <Box
                                key={optIndex}
                                sx={{
                                  p: 1.5,
                                  mb: 1,
                                  borderRadius: 1,
                                  border: '2px solid',
                                  borderColor: isCorrectOption ? 'success.main' : isSelected ? 'error.main' : 'divider',
                                  bgcolor: isCorrectOption ? 'success.50' : isSelected ? 'error.50' : 'transparent',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }}
                              >
                                <Typography variant="body2" sx={{ fontWeight: isSelected || isCorrectOption ? 600 : 400 }}>
                                  {String.fromCharCode(65 + optIndex)}. {option}
                                </Typography>
                                {isCorrectOption && (
                                  <Chip label="Correct Answer" size="small" color="success" sx={{ ml: 'auto' }} />
                                )}
                                {isSelected && !isCorrectOption && (
                                  <Chip label="Selected" size="small" color="error" sx={{ ml: 'auto' }} />
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No quiz answers available
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewAnswersDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageClubPage;
